/**
 * Platform Admin Reset Password Page
 */

import React, { useState, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Shield, AlertCircle, Lock, ArrowLeft, CheckCircle, Eye, EyeOff } from 'lucide-react';
import { platformClient } from '../api/platformClient';

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [token, setToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const tokenParam = searchParams.get('token');

    if (emailParam) setEmail(decodeURIComponent(emailParam));
    if (tokenParam) setToken(tokenParam);

    if (!emailParam || !tokenParam) {
      setError('Invalid or missing reset link parameters');
    }
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    setIsLoading(true);

    try {
      await platformClient.resetPassword(email, token, password);
      setIsSuccess(true);
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to reset password');
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.header}>
            <CheckCircle size={48} color="#22c55e" />
            <h1 style={styles.title}>Password Reset Complete</h1>
            <p style={styles.subtitle}>
              Your password has been successfully reset. You can now sign in with your new password.
            </p>
          </div>

          <button
            onClick={() => navigate('/login')}
            style={styles.button}
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.header}>
          <Shield size={48} color="#6366f1" />
          <h1 style={styles.title}>Reset Password</h1>
          <p style={styles.subtitle}>
            Enter your new password below.
          </p>
        </div>

        <form onSubmit={handleSubmit} style={styles.form}>
          {error && (
            <div style={styles.error}>
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          <div style={styles.field}>
            <label style={styles.label}>Email</label>
            <input
              type="email"
              value={email}
              disabled
              style={{ ...styles.input, opacity: 0.7, cursor: 'not-allowed' }}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>New Password</label>
            <div style={styles.inputWrapper}>
              <Lock size={18} color="#64748b" style={styles.inputIcon} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                style={styles.inputWithIcon}
                placeholder="Enter new password"
                required
                minLength={8}
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeButton}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Confirm Password</label>
            <div style={styles.inputWrapper}>
              <Lock size={18} color="#64748b" style={styles.inputIcon} />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                style={styles.inputWithIcon}
                placeholder="Confirm new password"
                required
                minLength={8}
              />
            </div>
          </div>

          <div style={styles.requirements}>
            <p style={styles.requirementsTitle}>Password Requirements:</p>
            <ul style={styles.requirementsList}>
              <li style={{ color: password.length >= 8 ? '#22c55e' : '#94a3b8' }}>
                At least 8 characters
              </li>
            </ul>
          </div>

          <button
            type="submit"
            style={{
              ...styles.button,
              opacity: isLoading || !token ? 0.7 : 1,
              cursor: isLoading || !token ? 'not-allowed' : 'pointer',
            }}
            disabled={isLoading || !token}
          >
            {isLoading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>

        <Link to="/login" style={styles.backLink}>
          <ArrowLeft size={16} />
          <span>Back to Login</span>
        </Link>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#0f172a',
    padding: '1rem',
  },
  card: {
    backgroundColor: '#1e293b',
    borderRadius: '12px',
    padding: '2rem',
    width: '100%',
    maxWidth: '400px',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
  },
  header: {
    textAlign: 'center',
    marginBottom: '2rem',
  },
  title: {
    color: '#f8fafc',
    fontSize: '1.5rem',
    fontWeight: 700,
    margin: '0.5rem 0 0.5rem',
  },
  subtitle: {
    color: '#94a3b8',
    fontSize: '0.875rem',
    margin: 0,
    lineHeight: 1.5,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  label: {
    color: '#e2e8f0',
    fontSize: '0.875rem',
    fontWeight: 500,
  },
  input: {
    backgroundColor: '#334155',
    border: '1px solid #475569',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    color: '#f8fafc',
    fontSize: '1rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  inputWrapper: {
    position: 'relative',
  },
  inputIcon: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
  },
  inputWithIcon: {
    backgroundColor: '#334155',
    border: '1px solid #475569',
    borderRadius: '8px',
    padding: '0.75rem 2.5rem',
    color: '#f8fafc',
    fontSize: '1rem',
    outline: 'none',
    width: '100%',
    boxSizing: 'border-box',
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    color: '#64748b',
    cursor: 'pointer',
    padding: 0,
    display: 'flex',
    alignItems: 'center',
  },
  requirements: {
    backgroundColor: '#0f172a',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
  },
  requirementsTitle: {
    color: '#94a3b8',
    fontSize: '0.75rem',
    fontWeight: 600,
    margin: '0 0 0.5rem',
  },
  requirementsList: {
    margin: 0,
    padding: '0 0 0 1rem',
    fontSize: '0.75rem',
  },
  button: {
    backgroundColor: '#6366f1',
    color: 'white',
    border: 'none',
    borderRadius: '8px',
    padding: '0.875rem',
    fontSize: '1rem',
    fontWeight: 600,
    marginTop: '0.5rem',
    cursor: 'pointer',
  },
  error: {
    backgroundColor: '#7f1d1d',
    color: '#fecaca',
    padding: '0.75rem 1rem',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.875rem',
  },
  backLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.5rem',
    color: '#6366f1',
    textDecoration: 'none',
    fontSize: '0.875rem',
    marginTop: '1.5rem',
    paddingTop: '1rem',
    borderTop: '1px solid #334155',
  },
};
